import { Button, Typography } from "@joonapp/web-shared"
import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { useScreenTimeRestrictionsModalStore } from "./ScreenTimeRestrictionsModal"
import { CloseButton } from "../../../components/closeButton/CloseButton"
import SlideUpModal from "../../../components/slideUpModal/SlideUpModal"
import TimeInputNew from "../../../components/timeInput/TimeInputNew"
import { DAY_INDEX_MAP } from "../../../constants"

const DAY_FULL_KEYS = {
  monday: "DAY_FULL_MONDAY",
  tuesday: "DAY_FULL_TUESDAY",
  wednesday: "DAY_FULL_WEDNESDAY",
  thursday: "DAY_FULL_THURSDAY",
  friday: "DAY_FULL_FRIDAY",
  saturday: "DAY_FULL_SATURDAY",
  sunday: "DAY_FULL_SUNDAY",
} as const

interface RestrictionTimeRangeModalStore {
  isOpen: boolean
  onOpen: (day: string) => void
  onClose: () => void
  day: string
}

export const useRestrictionTimeRangeModalStore =
  create<RestrictionTimeRangeModalStore>((set) => ({
    isOpen: false,
    onOpen: (day: string) => set({ isOpen: true, day }),
    onClose: () => set({ isOpen: false }),
    day: "monday",
  }))

export const RestrictionTimeRangeModal = () => {
  const { isOpen, day, onClose } = useRestrictionTimeRangeModalStore()
  const { t } = useTranslation()
  const {
    exceptionTimeMapping,
    exceptionDays,
    setExceptionTimeMapping,
    setExceptionDays,
  } = useScreenTimeRestrictionsModalStore()
  const [start, setStart] = useState("18:00")
  const [end, setEnd] = useState("20:00")

  useEffect(() => {
    if (!exceptionTimeMapping[day]) {
      setStart("18:00")
      setEnd("20:00")
      return
    }
    setStart(exceptionTimeMapping[day].start)
    setEnd(exceptionTimeMapping[day].end)
  }, [day, isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = () => {
    if (!start || !end) {
      toast.error(t("RTRM_ERR_BOTH_TIMES"))
      return
    }
    if (end <= start) {
      toast.error(t("RTRM_ERR_END_AFTER_START"))
      return
    }
    setExceptionTimeMapping(day, start, end)
    onClose()
  }

  const handleClose = () => {
    if (!exceptionTimeMapping[day]?.start && !exceptionTimeMapping[day]?.end) {
      const dayToSetIndex = DAY_INDEX_MAP[day as keyof typeof DAY_INDEX_MAP]
      setExceptionDays(
        exceptionDays.map((day, index) =>
          index === dayToSetIndex ? false : day
        )
      )
    }
    onClose()
  }

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={handleClose}
      backgroundType="overlay"
      size="fit-content"
    >
      <div className="w-full h-full flex flex-col align-start gap-4 p-4 bg-bgXLightGray">
        <CloseButton onClick={handleClose} />
        <Typography variant="bodyBold" className="w-[95%]">
          {t("RTRM_TITLE", {
            day: t(DAY_FULL_KEYS[day as keyof typeof DAY_FULL_KEYS]),
          })}
        </Typography>
        <div className="w-full flex flex-col gap-2">
          <Typography variant="bodySmall">{t("RTRM_START_TIME")}</Typography>
          <div className="mr-auto">
            <TimeInputNew value={start} setValue={setStart} />
          </div>
          <Typography variant="bodySmall">{t("RTRM_END_TIME")}</Typography>
          <div className="mr-auto">
            <TimeInputNew value={end} setValue={setEnd} />
          </div>
        </div>
        <Button text={t("QUEST_SAVE")} onClick={onSave} fullWidth />
      </div>
    </SlideUpModal>
  )
}
